import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check } from 'lucide-react';

/**
 * LandingSurvey — custom 1:1 replacement for the native PostHog
 * "Landing — what's holding you back?" survey (019eea67). Self-contained
 * bottom-right popover on the landing: 20s delay, 30-day client-side dedup,
 * single-choice (+ open "other"). First-party (ad-blocker proof): posts to
 * /api/v1/feedback, mirrored to the PostHog survey server-side.
 *
 * Decoupled from the app shell (colors inlined) — the landing is its own
 * unauthenticated context, rendered outside AppShell.
 */

const SURVEY_ID = '019eea67-7077-0000-8520-7bc8473e14b9';
const SURVEY_NAME = "Landing — what's holding you back?";
const Q_CHOICE = 'ae6a0e9a-d83c-40a4-be0e-f61e8de32cd8';
const QUESTION = "Before you go — what's holding you back from trying Heyward?";
const CHOICES = [
  'Still figuring out if it fits my situation',
  'Just looking — not buying right now',
  'Not sure I can trust the analysis',
  'Sign-up / login was a hassle',
  'Price or cost concerns',
];
const OTHER = 'Something else';

const DELAY_MS = 20000;
const DEDUP_KEY = 'hw_landing_survey_seen_at';
const DEDUP_GAP_MS = 30 * 24 * 60 * 60 * 1000;

const API_BASE = import.meta.env.REACT_APP_API_URL || window.location.origin;
const FONT = 'Inter, system-ui, -apple-system, sans-serif';
const ease = [0.22, 1, 0.36, 1];

// Zolplay palette (inlined; matches the native survey appearance).
const K = {
  text: '#262626', mid: '#525252', mute: '#737373',
  border: '#e5e5e5', surface: '#ffffff', dark: '#292524', white: '#ffffff',
  successTint: '#dcfce7', successDark: '#15803d', danger: '#b91c1c',
};

const dedupDue = () => {
  try { return Date.now() - Number(localStorage.getItem(DEDUP_KEY) || 0) > DEDUP_GAP_MS; } catch { return true; }
};
const markSeen = () => { try { localStorage.setItem(DEDUP_KEY, String(Date.now())); } catch { /* ignore */ } };

export default function LandingSurvey() {
  const [open, setOpen] = useState(false);
  const [choice, setChoice] = useState('');
  const [otherText, setOtherText] = useState('');
  const [view, setView] = useState('form'); // 'form' | 'sending' | 'done'
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!dedupDue()) return undefined;
    const t = setTimeout(() => setOpen(true), DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const close = useCallback(() => {
    markSeen();
    setOpen(false);
  }, []);

  const isOther = choice === OTHER;
  const answer = isOther ? otherText.trim() : choice;
  const canSubmit = !!answer && view !== 'sending';

  const submit = async () => {
    if (!canSubmit) return;
    setView('sending');
    setError(null);
    try {
      const distinctId = (typeof window !== 'undefined' && window.posthog?.get_distinct_id?.()) || '';
      const resp = await fetch(`${API_BASE}/api/v1/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          survey_id: SURVEY_ID,
          survey_name: SURVEY_NAME,
          source: 'custom_landing',
          responses: [{ id: Q_CHOICE, question: QUESTION, response: answer }],
          page_url: window.location.href,
          distinct_id: distinctId,
        }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      markSeen();
      setView('done');
      setTimeout(() => setOpen(false), 2600);
    } catch {
      setError("Couldn't send — please try again.");
      setView('form');
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="landing-survey"
          className="landing-survey"
          role="dialog"
          aria-label="One quick question"
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.98 }}
          transition={{ duration: 0.24, ease }}
          style={{
            position: 'fixed', right: 20, bottom: 20, zIndex: 2147483000,
            width: 'min(360px, calc(100vw - 32px))',
            background: K.surface, border: `1px solid ${K.border}`, borderRadius: 12,
            boxShadow: '0 16px 48px rgba(0,0,0,0.18)', overflow: 'hidden', fontFamily: FONT,
          }}
        >
          {view === 'done' ? (
            <div style={{ padding: '26px 22px', textAlign: 'center' }}>
              <div style={{ width: 40, height: 40, borderRadius: 999, margin: '0 auto 14px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: K.successTint, color: K.successDark }}>
                <Check size={20} strokeWidth={2.4} aria-hidden="true" />
              </div>
              <div className="landing-survey-done-title" style={{ fontSize: 15, fontWeight: 600, color: K.text, letterSpacing: '-0.01em' }}>Thank you — that genuinely helps.</div>
              <div className="landing-survey-done-note" style={{ marginTop: 6, fontSize: 13, lineHeight: 1.55, color: K.mute }}>We read every response.</div>
            </div>
          ) : (
            <div style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
                <span className="landing-survey-question" style={{ fontSize: 14.5, fontWeight: 600, color: K.text, letterSpacing: '-0.01em', lineHeight: 1.35 }}>{QUESTION}</span>
                <button className="landing-survey-dismiss" onClick={close} aria-label="Dismiss" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: K.mute, padding: 2, display: 'flex', flexShrink: 0 }}>
                  <X size={16} aria-hidden="true" />
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {[...CHOICES, OTHER].map((ch) => {
                  const selected = choice === ch;
                  return (
                    <button className="landing-survey-choice" key={ch} onClick={() => setChoice(ch)}
                      style={{
                        textAlign: 'left', width: '100%', cursor: 'pointer', padding: '10px 12px',
                        borderRadius: 8, fontSize: 13, fontFamily: FONT, transition: 'all 0.12s ease',
                        border: `1px solid ${selected ? K.dark : K.border}`,
                        background: selected ? K.dark : K.surface,
                        color: selected ? K.white : K.mid, fontWeight: selected ? 600 : 500,
                      }}>
                      {ch}
                    </button>
                  );
                })}
              </div>

              {isOther && (
                <input
                  className="landing-survey-other"
                  value={otherText}
                  onChange={(e) => setOtherText(e.target.value)}
                  placeholder="Tell us more (optional)…"
                  maxLength={4000}
                  autoFocus
                  style={{
                    marginTop: 8, width: '100%', boxSizing: 'border-box', padding: '10px 12px',
                    borderRadius: 8, fontSize: 13, fontFamily: FONT, color: K.text,
                    background: K.surface, border: `1px solid ${K.border}`, outline: 'none',
                  }}
                />
              )}

              {error && <div className="landing-survey-error" style={{ marginTop: 10, fontSize: 12.5, color: K.danger }}>{error}</div>}

              <button className="landing-survey-submit" onClick={submit} disabled={!canSubmit}
                style={{
                  marginTop: 16, width: '100%', cursor: canSubmit ? 'pointer' : 'not-allowed',
                  padding: '11px 14px', borderRadius: 8, border: 'none', background: K.dark,
                  color: K.white, fontFamily: FONT, fontSize: 13.5, fontWeight: 600,
                  letterSpacing: '-0.005em', opacity: canSubmit ? 1 : 0.45, transition: 'opacity 0.12s ease',
                }}>
                {view === 'sending' ? 'Sending…' : 'Send'}
              </button>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
